import { createContext, useContext, useEffect, useId, type ReactNode } from "react";
import { Icon } from "./Icon.js";

/** Contenedor del menú lateral donde las páginas pueden «portar» navegación contextual (p. ej. secciones del estudio). */
export const SidebarSlotContext = createContext<HTMLElement | null>(null);
export const useSidebarSlot = () => useContext(SidebarSlotContext);

/** Selector segmentado (alternativa compacta a radios / pares de botones). */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  size = "md",
}: {
  value: T;
  options: Array<{ value: T; label: ReactNode; testId?: string }>;
  onChange: (v: T) => void;
  label: string;
  size?: "sm" | "md";
}) {
  return (
    <div role="group" aria-label={label} className="inline-flex w-full rounded-full bg-slate-100 p-1 ring-1 ring-inset ring-slate-200">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            data-testid={o.testId}
            onClick={() => onChange(o.value)}
            className={`flex-1 rounded-full font-semibold transition ${size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm"} ${
              active ? "bg-white text-sky-800 shadow-sm ring-1 ring-slate-200" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Pestañas accesibles (role=tablist). El contenido lo pinta quien las usa según `value`. */
export function Tabs<T extends string>({
  value,
  tabs,
  onChange,
  label,
}: {
  value: T;
  tabs: Array<{ value: T; label: ReactNode; badge?: ReactNode }>;
  onChange: (v: T) => void;
  label: string;
}) {
  const id = useId();
  return (
    <div role="tablist" aria-label={label} className="flex gap-1 overflow-x-auto border-b border-slate-200">
      {tabs.map((t) => {
        const active = t.value === value;
        return (
          <button
            key={t.value}
            id={`${id}-${t.value}`}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => onChange(t.value)}
            className={`-mb-px inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              active ? "border-sky-700 text-sky-800" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {t.label}
            {t.badge !== undefined && t.badge !== null && (
              <span className={`rounded-full px-1.5 py-0.5 text-[0.7rem] leading-none ${active ? "bg-sky-100 text-sky-800" : "bg-slate-100 text-slate-600"}`}>{t.badge}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Panel lateral deslizable (derecha) para detalles, sin sacar al usuario de la lista. */
export function Drawer({ open, title, subtitle, onClose, children }: { open: boolean; title: ReactNode; subtitle?: ReactNode; onClose: () => void; children: ReactNode }) {
  const titleId = useId();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="presentation">
      <button type="button" aria-label="Cerrar panel" className="absolute inset-0 bg-ink/30 backdrop-blur-[1px]" onClick={onClose} />
      <aside role="dialog" aria-modal="true" aria-labelledby={titleId} className="relative flex h-full w-[min(92vw,34rem)] flex-col bg-white shadow-2xl">
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="truncate text-lg font-bold text-ink">
              {title}
            </h2>
            {subtitle && <div className="mt-0.5 text-sm text-slate-500">{subtitle}</div>}
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800" aria-label="Cerrar">
            <Icon name="close" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </aside>
    </div>
  );
}

/** Franja de aviso de una línea (en lugar de bloques grandes que empujan el contenido). */
export function NoticeStrip({ tone, icon = "info", children, testId }: { tone: "warning" | "info" | "synthetic"; icon?: "info" | "alert"; children: ReactNode; testId?: string }) {
  const cls =
    tone === "synthetic"
      ? "bg-fuchsia-50 text-fuchsia-950 ring-fuchsia-300"
      : tone === "warning"
        ? "bg-amber-50 text-amber-950 ring-amber-200"
        : "bg-sky-50 text-sky-950 ring-sky-200";
  return (
    <div role="note" data-testid={testId} className={`flex items-start gap-2 rounded-xl px-3 py-2 text-sm ring-1 ring-inset ${cls}`}>
      <Icon name={icon} className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
